import React from 'react';
import { motion } from 'motion/react';
import { RefreshCw, Database, ShieldCheck, Zap, Cloud, History, Clock } from 'lucide-react';

export default function BusinessContinuityPolicy() {
  const lastUpdated = "April 21, 2026";

  const continuityPillars = [
    {
      title: "System Redundancy",
      description: "Critical infrastructure is distributed across multiple availability zones to ensure that a single point of failure never results in total service disruption.",
      icon: Cloud
    },
    {
      title: "Automated Backups",
      description: "We maintain a rigorous backup schedule for all production data. Backups are encrypted and stored in geographically isolated, secure environments.",
      icon: Database
    },
    {
      title: "Recovery Procedures",
      description: "Our recovery playbooks are tested quarterly to ensure that data can be restored with 100% integrity and minimal latency during an actual event.",
      icon: RefreshCw
    },
    {
      title: "Operational Resilience",
      description: "Agrandintl prioritizes 'High Availability' design, allowing systems to self-heal and failover automatically when localized incidents occur.",
      icon: Zap
    }
  ];

  return (
    <div className="min-h-screen bg-background pt-32 pb-20">
      <div className="container mx-auto px-4 md:px-8">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-4xl mx-auto space-y-12"
        >
          {/* Header */}
          <div className="space-y-4 text-center">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent/5 border border-accent/10 text-accent text-[10px] font-bold uppercase tracking-[0.3em]">
              Operational Resilience
            </div>
            <h1 className="text-4xl md:text-6xl font-display font-bold tracking-tight">
              Business <span className="text-accent">Continuity</span>
            </h1>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              Agrandintl is committed to ensuring total service continuity and rapid recovery in the event of any operational disruption.
            </p>
          </div>

          {/* Resilience Visual */}
          <div className="glass-card p-12 rounded-[2.5rem] border-white/5 bg-white/[0.01] relative overflow-hidden text-center group">
            <div className="absolute inset-0 bg-gradient-to-b from-accent/5 to-transparent pointer-events-none" />
            <div className="space-y-6 relative z-10">
              <div className="w-16 h-16 rounded-3xl bg-accent/10 flex items-center justify-center text-accent mx-auto mb-4 border border-accent/20">
                <ShieldCheck className="w-8 h-8" />
              </div>
              <h2 className="text-2xl font-display font-bold">Uninterrupted Operations</h2>
              <p className="text-muted-foreground leading-relaxed text-base max-w-2xl mx-auto">
                "We don't just plan for failure; we architect for survival. Our business continuity framework ensures that your critical operations remain resilient, regardless of external circumstances."
              </p>
            </div>
          </div>

          {/* Pillars Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {continuityPillars.map((pillar, idx) => (
              <motion.div
                key={pillar.title}
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: idx * 0.1 }}
                className="glass-card p-8 rounded-3xl border-white/5 bg-white/[0.01] hover:border-accent/10 transition-all group"
              >
                <div className="space-y-4">
                  <div className="w-12 h-12 rounded-2xl bg-white/5 flex items-center justify-center text-accent group-hover:bg-accent group-hover:text-background transition-colors duration-500">
                    <pillar.icon className="w-6 h-6" />
                  </div>
                  <h3 className="text-xl font-display font-bold">{pillar.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {pillar.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Recovery Metrics */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[
              { icon: History, label: "RPO", value: "< 15 Minutes" },
              { icon: Clock, label: "RTO", value: "< 4 Hours" },
              { icon: Database, label: "Backups", value: "Hourly" }
            ].map((item) => (
              <div key={item.label} className="p-6 rounded-2x border border-white/5 bg-white/[0.02] flex items-center justify-between group">
                <div className="space-y-1">
                  <div className="text-[10px] uppercase font-bold tracking-widest text-muted-foreground">{item.label}</div>
                  <div className="text-lg font-display font-bold group-hover:text-accent transition-colors">{item.value}</div>
                </div>
                <item.icon className="w-5 h-5 text-accent/40" />
              </div>
            ))}
          </div>

          {/* Footer Metadata */}
          <div className="pt-12 border-t border-white/5 text-center">
            <div className="inline-flex items-center gap-2 mb-4 px-3 py-1 rounded bg-accent/5 text-[9px] font-bold tracking-tighter text-accent uppercase border border-accent/20">
              Resiliency Verified
            </div>
            <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-muted-foreground/60">
              Policy ID: AG-BC-26 | {lastUpdated}
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
